'use client';

import React, { useState } from 'react';
import { Text, Badge, Modal, ScrollArea } from '@mantine/core';
import SignUpForm from '@components/SignUpForm';
import { FaCheck, FaMinus } from 'react-icons/fa6';

// ─── Data ────────────────────────────────────────────────────────────────────

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: '฿4,888',
    tagline: 'Get started with a website, social marketing & mobile app for up to 3 agents.',
    featured: false,
    highlights: [
      '1 admin + 3 agent accounts',
      '200 listings & 200 social posts',
      'Modern SEO website + mobile app',
      'English, Thai + 1 language',
      'AI listing descriptions & social posts',
      'Ticket support — 2 business days',
    ],
    addons: ['Proppit Live Connect ฿188/mo', 'Setup fee ฿4,888'],
  },
  {
    id: 'small-office',
    name: 'Small Office',
    price: '฿6,888',
    tagline: 'For growing teams — 5 agents, 500 listings, and full social automation.',
    featured: false,
    highlights: [
      '5 users + 5 social posting accounts',
      '500 listings & 500 social posts',
      'Projects, blogs & listing export',
      'English, Thai + 2 languages',
      'FB-to-listing & AI translations',
      'Priority ticket — 1 business day',
    ],
    addons: ['Proppit Live Connect ฿188/mo', 'Setup fee ฿4,888'],
  },
  {
    id: 'office-pro',
    name: 'Office Pro',
    price: '฿9,888',
    tagline: 'Unlimited users, listings & posts — with AI power tools and portal integrations.',
    featured: true,
    highlights: [
      'Up to 50 users, unlimited listings & unlimited social posts',
      'English, Thai + 4 languages',
      'Magic Mailbox AI',
      'PDF to Project AI Power Tool',
      // 'AI auto-blogging & project farming',
      'Live chat + priority ticketing',
    ],
    addons: ['Extra social accounts ฿200/mo', 'Extra language from ฿1,000', 'Setup fee ฿4,888'],
  },
];

// ─── Comparison table rows ────────────────────────────────────────────────────
// value: true = check, false = dash, string = custom label

const tableRows = [
  { section: 'Accounts & Limits' },
  { label: 'Admin accounts', starter: '1', smallOffice: '5', officePro: '50' },
  { label: 'Agent accounts', starter: '3', smallOffice: '5', officePro: 'Unlimited' },
  { label: 'Listings', starter: '200', smallOffice: '500', officePro: 'Unlimited' },
  { label: 'Social posts / month', starter: '200', smallOffice: '500', officePro: 'Unlimited' },
  { label: 'Social posting accounts', starter: '3', smallOffice: '5', officePro: '5' },

  { section: 'Website & Marketing' },
  { label: 'Modern SEO website', starter: true, smallOffice: true, officePro: true },
  { label: 'Your own domain', starter: true, smallOffice: true, officePro: true },
  { label: 'Mobile app', starter: true, smallOffice: true, officePro: true },
  { label: 'Secure Back Office', starter: true, smallOffice: true, officePro: true },
  { label: 'Manual & automated social posts', starter: true, smallOffice: true, officePro: true },
  { label: 'LINE, WhatsApp, Telegram listing share', starter: true, smallOffice: true, officePro: true },
  { label: 'LINE team notifications', starter: true, smallOffice: true, officePro: true },
  { label: 'Blogs', starter: false, smallOffice: true, officePro: true },
  { label: 'Projects/New Developments', starter: false, smallOffice: true, officePro: true },
  { label: 'Secure Document Upload', starter: false, smallOffice: true, officePro: true },
  { label: 'Listing export (CSV/Excel)', starter: false, smallOffice: true, officePro: true },
  { label: 'Customizable areas, landmarks & amenities', starter: false, smallOffice: false, officePro: true },
  { label: 'Auto testimonial import', starter: false, smallOffice: false, officePro: true },
  { label: 'Google Tags (Analytics/SEO)', starter: false, smallOffice: false, officePro: true },
  { label: 'SEO Optimized Landing Pages', starter: false, smallOffice: false, officePro: true },

  { section: 'Languages' },
  { label: 'Auto-translate languages', starter: 'English, Thai + 1', smallOffice: 'English, Thai + 2', officePro: 'English, Thai + 4' },

  { section: 'CRM & Management' },
  { label: 'Lead management', starter: true, smallOffice: true, officePro: true },
  { label: 'Agent management', starter: true, smallOffice: true, officePro: true },
  { label: 'Rental lease management', starter: true, smallOffice: true, officePro: true },
  { label: 'Secure document storage', starter: false, smallOffice: false, officePro: true },
  { label: 'Data export (XML/Excel/CSV/JSON)', starter: false, smallOffice: false, officePro: true },
  { label: 'Google Drive photo archive', starter: false, smallOffice: false, officePro: true },
  { label: 'Portal integrations', starter: false, smallOffice: false, officePro: true },
  { label: '"Styler" site CMS (by request)*', starter: false, smallOffice: false, officePro: true },

  { section: 'AI Tools' },
  { label: 'AI listing descriptions', starter: true, smallOffice: true, officePro: true },
  { label: 'Auto-translations', starter: true, smallOffice: true, officePro: true },
  { label: 'Text-to-listing', starter: true, smallOffice: true, officePro: true },
  { label: 'FB-to-listing', starter: true, smallOffice: true, officePro: true },
  { label: 'Text-to-lead & chat share-to-lead', starter: true, smallOffice: true, officePro: true },
  { label: 'Alternate properties', starter: true, smallOffice: true, officePro: true },
  { label: 'AI social post creation', starter: true, smallOffice: true, officePro: true },
  { label: 'Magic Mailbox AI assistant', starter: false, smallOffice: false, officePro: true },
  { label: 'PDF-to-Project AI assistant', starter: false, smallOffice: false, officePro: true },
  { label: 'AI auto listing & project farming*', starter: false, smallOffice: false, officePro: true },
  { label: 'AI auto-blogging*', starter: false, smallOffice: false, officePro: true },

  { section: 'Training' },
  { label: 'Onboarding sessions', starter: '1 session', smallOffice: '2 sessions', officePro: '2 sessions' },
  { label: 'Free video tutorials', starter: true, smallOffice: true, officePro: true },

  { section: 'Support' },
  { label: 'Support channel', starter: 'Ticket', smallOffice: 'Priority ticket', officePro: 'Live chat + priority ticket' },
  { label: 'Response time', starter: '2 business days', smallOffice: '1 business day', officePro: 'Same day' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const HighlightItem = ({ children }) => (
  <div className='flex items-start gap-2 mb-2'>
    <FaCheck color='#019DA4' className='mt-[3px] shrink-0' size={12} />
    <Text size='sm' lh={1.5}>{children}</Text>
  </div>
);

const CellValue = ({ value }) => {
  if (value === true) return <FaCheck color='#019DA4' size={14} className='mx-auto' />;
  if (value === false) return <FaMinus color='#ccc' size={14} className='mx-auto' />;
  return <span className='text-sm text-gray-700'>{value}</span>;
};

const ComparisonTable = () => (
  <div className='overflow-x-auto'>
    <table className='w-full text-sm border-collapse'>
      <thead>
        <tr>
          <th className='text-left py-3 pr-4 font-semibold text-gray-500 w-2/5'></th>
          {plans.map(p => (
            <th key={p.id} className='py-3 px-2 text-center font-semibold'>
              <div className={p.featured ? 'text-[#019DA4]' : 'text-gray-800'}>{p.name}</div>
              <div className='text-base font-bold'>
                {p.price}<span className='text-xs font-normal text-gray-400'>/mo</span>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {tableRows.map((row, i) => {
          if (row.section) {
            return (
              <tr key={i}>
                <td colSpan={4} className='pt-5 pb-1'>
                  <span className='text-[11px] font-semibold uppercase tracking-widest text-gray-400'>
                    {row.section}
                  </span>
                </td>
              </tr>
            );
          }
          return (
            <tr key={i} className='border-t border-gray-100 hover:bg-gray-50 transition-colors'>
              <td className='py-2 pr-4 text-gray-600'>{row.label}</td>
              <td className='py-2 px-2 text-center'><CellValue value={row.starter} /></td>
              <td className='py-2 px-2 text-center bg-[#96e3e7]/5'><CellValue value={row.smallOffice} /></td>
              <td className='py-2 px-2 text-center bg-[#019DA4]/5'><CellValue value={row.officePro} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
    <div className='flex justify-end'>
      <Text size='xs' c='gray' mt={18} lh={1.5}>*Under development</Text>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const NewPricingModal = () => {
  const [tableOpen, setTableOpen] = useState(false);

  return (
    <div id='pricing' className='w-full'>
      <div className='md:w-10/12 w-full max-w-[1600px] mx-auto py-10'>
        <div className='sm:px-0 px-2'>
          <h2 className='welist-title mb-10 text-3xl text-center font-bold tracking-wider'>
            Low Prices. Maximum Value.
          </h2>
        </div>

        {/* Pricing cards */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 sm:px-0 px-2'>
          {plans.map(plan => (
            <div
              key={plan.id}
              className={`h-full w-full rounded-md p-4 bg-white/10 backdrop-blur-md flex flex-col ${
                plan.featured ? 'border-2 border-[#019DA4]' : 'border border-gray-200'
              }`}
              style={{
                boxShadow:
                  '0 10px 32px rgba(34,42,53,0.12), 0 1px 1px rgba(0,0,0,0.05), 0 0 0 1px rgba(34,42,53,0.05), 0 4px 6px rgba(34,42,53,0.08), 0 24px 108px rgba(47,48,55,0.10)',
              }}
            >
              <h3 className='welist-title tracking-wider text-xl font-semibold mb-1'>{plan.name}</h3>
              <div className='mb-1'>
                <span className='text-2xl font-semibold'>{plan.price}</span>
                <span className='text-gray-500 text-sm'>/month</span>
              </div>
              <p className='text-sm text-gray-500 mb-3 pb-3 border-b border-gray-200 leading-snug'>
                {plan.tagline}
              </p>
              <div className='flex-1 mb-4'>
                {plan.highlights.map(h => <HighlightItem key={h}>{h}</HighlightItem>)}
              </div>
              <div className='mt-auto pt-3 border-t border-gray-100'>
                {plan.addons.map(a => (
                  <Text key={a} size='xs' c='gray' mb={2}>*{a}</Text>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Compare link */}
        <div className='flex justify-center mt-6'>
          <button
            onClick={() => setTableOpen(true)}
            className='text-sm text-[#019DA4] underline underline-offset-2 hover:opacity-70 transition-opacity'
          >
            Compare all plans
          </button>
        </div>

        <div className='text-right text-gray-500 mt-4 sm:px-0 px-2 text-sm'>
          *All prices include VAT
        </div>

        <div className='w-full flex items-center justify-center mt-6'>
          <SignUpForm title='Subscribe Now' size='lg' bg='blue' formType='pricing' />
        </div>
      </div>

      {/* Comparison modal */}
      <Modal
        opened={tableOpen}
        onClose={() => setTableOpen(false)}
        title={<span className='font-semibold text-lg'>Compare all features</span>}
        size='xl'
        scrollAreaComponent={ScrollArea.Autosize}
      >
        <ComparisonTable />
        <div className='flex justify-center mt-6 mb-2'>
          <SignUpForm title='Subscribe Now' size='md' bg='blue' formType='pricing' />
        </div>
      </Modal>
    </div>
  );
};

export default NewPricingModal;